import { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'
import { productsAPI } from '../../services/productsAPI'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, Shield } from 'lucide-react'

export default function AddRoleModal({ isOpen, onClose, onSubmit, role = null, isSubmitting = false }) {
  const isEditMode = !!role
  const [selectedPermissions, setSelectedPermissions] = useState({})
  const [roleName, setRoleName] = useState('')
  const [roleDescription, setRoleDescription] = useState('')
  const [errors, setErrors] = useState({})
  const permissionsInitializedRef = useRef(false)

  // Fetch permissions from product_features table
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery(
    ['hubPermissions'],
    () => productsAPI.getHubPermissions(),
    {
      enabled: isOpen,
      select: (response) => response.data?.data?.permissions || [],
      onError: (error) => {
        console.error('Error fetching permissions:', error)
      }
    }
  )

  const permissions = permissionsData || []

  // Format permissions as resource.action - use useMemo to prevent infinite loops
  const formattedPermissions = useMemo(() => {
    return permissions
      .filter(p => {
        // Check if permission has resource and valid permissions object
        if (!p.resource || !p.permissions || typeof p.permissions !== 'object') {
          return false
        }
        // Check if there's at least one permission key with value true, or has action field
        const permissionKeys = Object.keys(p.permissions).filter(key => 
          p.permissions[key] === true && key !== 'action'
        )
        // Also allow if it has action field (old format)
        return permissionKeys.length > 0 || p.permissions.action
      })
      .map(p => {
        // Extract permission key from permissions object
        // Handle new format: {"organizations.create": true}
        const permissionKeys = Object.keys(p.permissions).filter(key => 
          p.permissions[key] === true && key !== 'action'
        )
        
        let permissionKey = null
        if (permissionKeys.length > 0) {
          // New format: use the permission key directly
          permissionKey = permissionKeys[0]
        } else if (p.permissions.action && p.resource) {
          // Old format: {"action": "create"} - construct from resource and action
          permissionKey = `${p.resource}.${p.permissions.action}`
        }
        
        if (!permissionKey) return null
        
        // Split permission key to get resource and action
        const parts = permissionKey.split('.')
        const resource = parts[0] || p.resource
        const action = parts[1] || permissionKey
        
        return {
          id: p.id,
          key: permissionKey, // Use the actual permission key (e.g., "organizations.create")
          resource: resource,
          action: action,
          name: p.name,
          description: p.description
        }
      })
      .filter(p => p !== null) // Remove any null entries
  }, [permissions])

  // Initialize form when modal opens or role changes
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes
      setRoleName('')
      setRoleDescription('')
      setSelectedPermissions({})
      setErrors({})
      permissionsInitializedRef.current = false
      return
    }

    // Initialize form fields
    if (role) {
      setRoleName(role.name || '')
      setRoleDescription(role.description || '')
    } else {
      setRoleName('')
      setRoleDescription('')
      setSelectedPermissions({})
    }
    setErrors({})
    permissionsInitializedRef.current = false
  }, [isOpen, role?.id]) // Only depend on isOpen and role.id

  // Update selected permissions when formattedPermissions is ready (for edit mode)
  useEffect(() => {
    if (!isOpen || !role || formattedPermissions.length === 0 || permissionsInitializedRef.current) return

    const rolePermissions = typeof role.permissions === 'string' 
      ? JSON.parse(role.permissions) 
      : role.permissions || {}
    
    const selected = {}
    formattedPermissions.forEach(p => {
      if (rolePermissions[p.key]) {
        selected[p.key] = true
      }
    })
    setSelectedPermissions(selected)
    permissionsInitializedRef.current = true
  }, [isOpen, role?.id, formattedPermissions.length]) // Only depend on length, not the array itself

  const handlePermissionToggle = (permissionKey) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }))
  }

  const handleSelectAll = () => {
    const allSelected = {}
    formattedPermissions.forEach(p => {
      allSelected[p.key] = true
    })
    setSelectedPermissions(prev => ({ ...prev, ...allSelected }))
  }

  const handleUnselectAll = () => {
    setSelectedPermissions(prev => {
      const updated = { ...prev }
      formattedPermissions.forEach(p => {
        delete updated[p.key]
      })
      return updated
    })
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    
    // Validate
    const newErrors = {}
    if (!roleName || roleName.trim().length < 2) {
      newErrors.name = 'Role name is required and must be at least 2 characters'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Build permissions object with selected permissions
    const permissionsObj = {}
    Object.keys(selectedPermissions).forEach(key => {
      if (selectedPermissions[key]) {
        permissionsObj[key] = true
      }
    })

    const roleData = {
      name: roleName.trim(),
      description: roleDescription.trim(),
      permissions: permissionsObj
    }

    try {
      await onSubmit(roleData)
      setRoleName('')
      setRoleDescription('')
      setSelectedPermissions({})
      setErrors({})
    } catch (error) {
      console.error('Error submitting role:', error)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setRoleName('')
      setRoleDescription('')
      setSelectedPermissions({})
      setErrors({})
      onClose()
    }
  }

  const selectedCount = Object.values(selectedPermissions).filter(Boolean).length

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-secondary-600 bg-opacity-75" onClick={handleClose} />
        
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-lg">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-blue-600" />
                </div>
                <CardTitle>{isEditMode ? 'Edit Role' : 'Add New Role'}</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleFormSubmit} className="space-y-6">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    value={roleName}
                    onChange={(e) => {
                      setRoleName(e.target.value)
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: null }))
                      }
                    }}
                    placeholder="Enter role name"
                    disabled={isSubmitting || isEditMode}
                    className={`input w-full ${errors.name ? 'border-red-500' : ''} ${isEditMode ? 'bg-secondary-100 cursor-not-allowed' : ''}`}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={roleDescription}
                    onChange={(e) => setRoleDescription(e.target.value)}
                    rows={3}
                    placeholder="Enter role description"
                    disabled={isSubmitting}
                    className="input w-full resize-none"
                  />
                </div>

                {/* Permissions Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-secondary-700">
                      Permissions
                    </label>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={handleSelectAll}
                        disabled={isSubmitting || permissionsLoading}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Select All
                      </button>
                      <span className="text-secondary-400">|</span>
                      <button
                        type="button"
                        onClick={handleUnselectAll}
                        disabled={isSubmitting || permissionsLoading}
                        className="text-sm text-secondary-600 hover:text-secondary-800 font-medium"
                      >
                        Unselect All
                      </button>
                    </div>
                  </div>

                  {/* Permissions List */}
                  {permissionsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : formattedPermissions.length > 0 ? (
                    <div className="border border-secondary-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-2">
                        {formattedPermissions.map((permission) => {
                          const isSelected = selectedPermissions[permission.key] || false
                          return (
                            <label
                              key={permission.key}
                              className="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary-50 cursor-pointer border border-secondary-200"
                            >
                              <div className="flex items-center pt-1">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handlePermissionToggle(permission.key)}
                                  disabled={isSubmitting}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-secondary-900">
                                  {permission.key}
                                </div>
                                {permission.name && (
                                  <div className="text-xs text-secondary-600 mt-1">
                                    {permission.name}
                                  </div>
                                )}
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="border border-secondary-200 rounded-lg p-8 text-center">
                      <p className="text-sm text-secondary-600">
                        No permissions available.
                      </p>
                    </div>
                  )}

                  {/* Selected Count */}
                  <div className="mt-4 text-sm text-secondary-600">
                    Selected: {selectedCount} permission{selectedCount !== 1 ? 's' : ''}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || permissionsLoading}
                  >
                    {isSubmitting ? (
                      'Saving...'
                    ) : (
                      <>
                        <Shield className="h-4 w-4 mr-2" />
                        {isEditMode ? 'Update Role' : 'Create Role'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
